/**
 * UI locale: `localStorage` bootstrap key (`UI_LOCALE_STORAGE_KEY`) plus Settings `/account/preferences` `uiLocale`
 * when the API returns a value (account-scoped; mirrored to localStorage on save).
 * Resolution order for initial load: stored preference → `?lang=` / `?locale=` → `navigator.language` → `en`.
 */
export const UI_LOCALE_STORAGE_KEY = "oktogo_ui_locale";

/** Display languages shipped in the app (see `src/lib/i18n/index.ts` resources). */
export const SUPPORTED_UI_LOCALES = ["en", "de", "fr", "it", "es"] as const;

export type SupportedUiLocale = (typeof SUPPORTED_UI_LOCALES)[number];

/** End-user-facing names for the language selector (native names). */
export const LOCALE_NATIVE_LABELS: Record<SupportedUiLocale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  es: "Español",
};

export function isSupportedUiLocale(value: string | null | undefined): value is SupportedUiLocale {
  return !!value && (SUPPORTED_UI_LOCALES as readonly string[]).includes(value);
}

export function readStoredUiLocale(): SupportedUiLocale | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(UI_LOCALE_STORAGE_KEY);
    if (!raw) return null;
    if (!isSupportedUiLocale(raw)) {
      try {
        window.localStorage.removeItem(UI_LOCALE_STORAGE_KEY);
      } catch {
        /* private mode */
      }
      return null;
    }
    return raw;
  } catch {
    return null;
  }
}

export function persistUiLocale(lng: SupportedUiLocale): void {
  try {
    window.localStorage.setItem(UI_LOCALE_STORAGE_KEY, lng);
  } catch {
    /* private mode / quota */
  }
}

/** Map i18next / browser tags like `de-DE` to a supported UI code. */
export function normalizeToSupportedUiLocale(lng: string): SupportedUiLocale {
  if (isSupportedUiLocale(lng)) return lng;
  const primary = (lng.split(/[-_]/)[0] ?? "").toLowerCase();
  if (isSupportedUiLocale(primary)) return primary;
  return "en";
}

/**
 * Read `lang` or `locale` from a query string (e.g. `window.location.search`).
 * Returns null if missing or not supported (after normalizing the primary subtag).
 */
export function parseLocaleFromUrlSearch(search: string): SupportedUiLocale | null {
  const q = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  const raw = params.get("lang") ?? params.get("locale");
  if (!raw) return null;
  const primary = raw.split(/[-_]/)[0]?.toLowerCase() ?? "";
  if (isSupportedUiLocale(primary)) return primary;
  if (isSupportedUiLocale(raw)) return raw;
  const normalized = normalizeToSupportedUiLocale(raw);
  return normalized === "en" ? null : normalized;
}

/** Pick first supported language from `navigator.languages` / `navigator.language`. */
export function inferUiLocaleFromNavigator(): SupportedUiLocale | null {
  if (typeof navigator === "undefined") return null;
  const candidates = [...(navigator.languages ?? []), navigator.language];
  for (const tag of candidates) {
    if (!tag) continue;
    const primary = tag.split(/[-_]/)[0]?.toLowerCase() ?? "";
    if (isSupportedUiLocale(primary)) return primary;
  }
  return null;
}

/**
 * Initial UI language: stored preference wins; else URL; else browser; else `en`.
 * `shouldPersist` is true when the value came from URL or browser (not yet stored).
 */
export function resolveInitialUiLocale(): { lng: SupportedUiLocale; shouldPersist: boolean } {
  const stored = readStoredUiLocale();
  if (stored) return { lng: stored, shouldPersist: false };

  if (typeof window === "undefined") {
    return { lng: "en", shouldPersist: false };
  }

  const fromUrl = parseLocaleFromUrlSearch(window.location.search);
  if (fromUrl) return { lng: fromUrl, shouldPersist: true };

  const fromNav = inferUiLocaleFromNavigator();
  if (fromNav) return { lng: fromNav, shouldPersist: false };

  return { lng: "en", shouldPersist: false };
}
